import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { SeattleGameState } from '../../types';
import { computeTotalExpenses } from '../../types';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 7: 費用支払フェーズ（Seattle固有）
 *
 * 通常の維持費に加え、貨物輸送レベルに応じた維持費を支払います。
 *
 * 維持費計算:
 * - 株式配当: sharesIssued × $1
 * - 機関車維持費: LOCOMOTIVE_COSTS[locomotiveLevel]
 * - 貨物輸送レベル維持費: cargoTransportExpenses[cargoTransportLevel]
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_07_pay_expenses_seattle.md
 */
export const payExpensesSeattlePhase: PhaseConfig<SeattleGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G }: { G: SeattleGameState }) => {
    debug.log('[payExpensesSeattlePhase] Phase 7: Pay Expenses (Seattle) started');

    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      G.autoPhaseResult = null;
      return;
    }

    // 経費計算（共通関数）
    const totalExpense = computeTotalExpenses(G);

    debug.log(`[payExpensesSeattlePhase] Expenses: $${totalExpense}`);

    // 支払処理
    if (player.cash >= totalExpense) {
      player.cash -= totalExpense;
      debug.log(`[payExpensesSeattlePhase] Paid $${totalExpense}, remaining: $${player.cash}`);
    } else {
      const shortage = totalExpense - player.cash;
      player.cash = 0;
      player.incomeLevel -= shortage;
      debug.log(`[payExpensesSeattlePhase] Insufficient cash, shortage: $${shortage}, incomeLevel: ${player.incomeLevel}`);
    }

    // 処理結果を保存
    G.autoPhaseResult = {
      phase: 'payExpenses',
      message: 'Paying expenses...',
      floatingText: `-$${totalExpense}`,
      details: {
        expenses: totalExpense,
      },
    };
  },

  moves: {
    proceedAutoPhase: ({ G, events }: { G: SeattleGameState; events: any }) => {
      G.autoPhaseResult = null;
      events.endPhase();
    },
    undoToTurnStart: undoToTurnStart as any,
  },

  endIf: ({ G }: { G: SeattleGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[payExpensesSeattlePhase] Force transition to issueShares (Undo)');
      return true;
    }

    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      return true;
    }
    return false;
  },

  onEnd: ({ G }: { G: SeattleGameState }) => {
    debug.log('[payExpensesSeattlePhase] Phase 7 ended');
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('payExpenses');
  },

  // Phase 8 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: SeattleGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'incomeReduction';
  }
};
